import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import Select, { StylesConfig } from 'react-select';
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import { ShowSwalWithConfirmation } from "../../../common/SweetAlert";
import { KTIcon } from "../../../_metronic/helpers";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ShowToast } from "../../../common/Toastr";
import { useTranslation } from "react-i18next";
import CustomPagination from "../../../common/Pagination";
import { getPagePermissionsAsync } from '../../../common/GlobalSlice';
import Loader from "../../../common/Loader";
import fetchTimeliveAPI from "../../../common/fetchTimeliveAPI";
import { getPageSizeArray, setItemStatus, paginationStyles, sortData, getValueFromResource, dateFormatByAccountCulture, setActiveStatus } from '../../../common/timeLiveFunction';
import PageSizeSelect from '../../../common/PageSizeSelect';
import { Dropdown } from 'react-bootstrap';
import clsx from "clsx";
import { getAttendancePoliciesListAsync, deleteAccountAttendancePolicy, clearPolicyStatus } from "./AttendancePolicySlice";

const AttendancePolicyList = (props: any) => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { pageId, menuList, sessionAccountEmployeeId, CurrentCultureDateFormat, cultureInfoName } = useAppSelector((state) => state.globalState);
    const { policyList, isLoading: reduxLoading } = useAppSelector((state) => state.attendancePolicy);

    const [attendancePolicyList, setAttendancePolicyList] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [showArchived, setShowArchived] = useState(false); // <--- State for Toggle

    const [sortColumn, setSortColumn] = useState('AttendancePolicyName');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    const [permissions, setPermissions] = useState({
        IsList: true,
        IsAdd: true,
        IsEdit: true,
        IsDelete: true
    });

    useEffect(() => {
        dispatch(getAttendancePoliciesListAsync());
    }, [dispatch]);

    useEffect(() => {
        if (policyList) {
            const mappedList = policyList.map((item: any) => ({
                ...item,
                isSelected: item.isSelected ?? 0
            }));
            setAttendancePolicyList(mappedList);
        }
    }, [policyList]);

    useLayoutEffect(() => {
        const fetchPagePermissions = async () => {
            if (pageId && pageId !== '') {
                const res = await dispatch(getPagePermissionsAsync(pageId));
                if (!res.payload.IsList) {
                    navigate('../error/403');
                } else {
                    setPermissions(res.payload);
                }
            } else if (!pageId && menuList && menuList.length > 0) {
                const routeName = location.pathname.split('/').filter(Boolean)[0];
                const foundItem = menuList.find(item => item.SystemCategoryPageFrontEnd === routeName);
                if (foundItem) {
                    const res = await dispatch(getPagePermissionsAsync(foundItem.SystemSecurityCategoryPageId));
                    if (!res.payload.IsList) {
                        navigate('../error/403');
                    } else {
                        setPermissions(res.payload);
                    }
                }
            }
        };
        fetchPagePermissions();
    }, [dispatch, location.pathname, menuList, navigate, pageId]);

    const handlePageChange = (pageNumber: any) => {
        setCurrentPage(pageNumber);
    };

    const handleSearch = (event: any) => {
        const { value } = event.target;
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageSizeChange = (selectedOption: any) => {
        const { value } = selectedOption;
        setPageSize(Number(value));
        setCurrentPage(1);
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, rowId: number) => {
        const isChecked = e.target.checked;
        setAttendancePolicyList(prev =>
            prev.map(item => item.AccountAttendancePolicyId === rowId ? { ...item, isSelected: isChecked ? 1 : 0 } : item)
        );
    };

    const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = e.target.checked;
        setAttendancePolicyList(prev =>
            prev.map(item => ({ ...item, isSelected: isChecked ? 1 : 0 }))
        );
    };

    const onHandleClientSideSorting = (order: 'asc' | 'desc', column: string) => {
        setSortColumn(column);
        setSortOrder(order);
    };

    const handleOnClickAttendancePolicyEntry = () => {
        dispatch(clearPolicyStatus());
        localStorage.setItem('objAttendancePolicyItem', '');
        navigate(`/attendancePolicy`);
    };

    const navigateToAttendancePolicyEntries = (obj: any) => {
        localStorage.setItem('objAttendancePolicyItem', JSON.stringify(obj));
        navigate(`/attendancePolicy/${obj}`);
    };


    const onDeleteClick = (policy: any) => {
        const isDraft = policy.AttendancePolicyStatusId === 2;
        if (!isDraft) {
            ShowToast("Warning", "Only draft policies can be deleted.", "warning", "toast-top-right");
            return;
        }
        ShowSwalWithConfirmation('Are you sure you want to delete this record?', "")
            .then((result) => {
                if (result) {
                    dispatch(deleteAccountAttendancePolicy(policy.AccountAttendancePolicyId))
                        .unwrap()
                        .then((res) => {
                            if (res?.StatusCode === '200') {
                                ShowToast("Success", res?.ResponseMessage, "success", "toast-top-right");
                                dispatch(getAttendancePoliciesListAsync());
                            } else {
                                ShowToast("Error", res?.ResponseMessage, "error", "toast-top-right");
                            }
                        })
                        .catch(() => ShowToast("Error", "Failed to delete item", "error", "toast-top-right"));
                }
            })
            .catch((error) => {
                console.log(error);
            });
    };

    const onDeleteSelectedClick = () => {
        const selectedIds = attendancePolicyList.filter((item) => item.isSelected === 1).map((item) => item.AccountAttendancePolicyId);
        if (selectedIds.length === 0) return;

        ShowSwalWithConfirmation('Are you sure you want to delete this record?', "")
            .then(async (result) => {
                if (result) {
                    setIsLoading(true);
                    try {
                        for (const id of selectedIds) {
                            await fetchTimeliveAPI(`api/AttendancePolicies/DeletePolicy/${id}`, "DELETE", "", true);
                        }
                        ShowToast("Attendance Policies deleted Successfully", 'Success', "success", "toast-top-right");
                        dispatch(getAttendancePoliciesListAsync());
                    } catch (error) {
                        ShowToast('Error deleting selected items', 'error', "error", "toast-top-right");
                    } finally {
                        setIsLoading(false);
                    }
                }
            }).catch((error) => console.log(error));
    };

    const filteredData = attendancePolicyList.filter((item: any) => {
        const searchTermLowerCase = searchTerm.toLowerCase();

        const matchesSearch =
            item.AttendancePolicyName?.toLowerCase().includes(searchTermLowerCase) ||
            item.Description?.toLowerCase().includes(searchTermLowerCase);

        const isArchived = item.AttendancePolicyStatusId === 3 || item.Status === "Archived";
        const matchesArchiveFilter = showArchived ? true : !isArchived;

        return matchesSearch && matchesArchiveFilter;
    });

    const indexOfLastItem = currentPage * pageSize;
    const indexOfFirstItem = indexOfLastItem - pageSize;
    const currentItems = sortData(filteredData, sortOrder, sortColumn).slice(indexOfFirstItem, indexOfLastItem);
    const totalItems = filteredData.length;

    return (
        <>
            {(isLoading || reduxLoading) && <Loader />}
            <div className="card card-custom admin-card">
                <div className="card-header card-drop sticky-top bg-white fixed-top">
                    <h3 className="card-title fw-bold headerTitle">
                        {getValueFromResource('Attendance Policy List', dispatch, t)}
                    </h3>
                    <div className="card-toolbar align-items-center">
                        <div className="form-check form-switch form-check-custom form-check-solid me-0">
                            <label className="fs-7 fw-normal" style={{ marginRight: 5 }}>
                                {getValueFromResource("Show Archived :", dispatch, t)}
                            </label>
                            <input
                                type="checkbox"
                                checked={showArchived}
                                onChange={(e) => {
                                    setShowArchived(e.target.checked);
                                    setCurrentPage(1);
                                }}
                                className="form-check-input h-20px w-40px cursor-pointer"
                            />
                        </div>
                        <div className="bullet bg-secondary h-35px w-1px mx-3"></div>
                        <div className="position-relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearch}
                                placeholder={getValueFromResource('Search', dispatch, t)}
                                className="form-control h-35px w-200px fs-7 fw-normal pr-4"
                                data-kt-user-table-filter="search"
                            />
                            <i className="ki-duotone ki-magnifier fs-5 position-absolute top-50 translate-middle-y end-0" style={{ marginRight: '10px' }}>
                                <span className="path1"></span>
                                <span className="path2"></span>
                            </i>
                        </div>
                        <div className='h-35px w-1px mx-1'></div>
                        <div className="d-flex align-items-center">
                            <PageSizeSelect onChange={handlePageSizeChange} />
                        </div>
                        {currentItems.length > 0 && (
                            <>
                                <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                                <div>
                                    <CustomPagination
                                        currentPage={currentPage}
                                        onPageChange={handlePageChange}
                                        pageSize={pageSize}
                                        totalItems={totalItems}
                                        start={(currentPage - 1) * pageSize}
                                        t={t}
                                    />
                                </div>
                            </>
                        )}



                        {(permissions?.IsAdd || (permissions?.IsDelete && attendancePolicyList.some((item: any) => item.isSelected === 1))) ? (
                            <div className='bullet bg-secondary h-35px w-1px mx-3'></div>
                        ) : null}

                        {permissions?.IsAdd &&
                            <button type="button"
                                className="btn btn-primary btn-bs-btn-font-weight btn-sm"
                                onClick={handleOnClickAttendancePolicyEntry}
                                style={{ marginRight: '2px', display: attendancePolicyList.every((item: any) => item.isSelected === 0) ? 'block' : 'none' }}
                            >
                                {getValueFromResource('Add Attendance Policy', dispatch, t)}
                            </button>
                        }

                        {permissions?.IsDelete &&
                            <>
                                <div className="text-muted me-5" style={{ marginRight: '2px', display: attendancePolicyList.some((item: any) => item.isSelected === 1) ? 'block' : 'none' }}>
                                    <span className="me-2" data-kt-user-table-select="selected_count">{attendancePolicyList.filter((item: any) => item.isSelected === 1).length}</span>
                                    {getValueFromResource('Selected', dispatch, t)}
                                </div>
                                <button type="button" className="btn btn-danger btn-sm" onClick={onDeleteSelectedClick}
                                    style={{ marginRight: '2px', display: attendancePolicyList.some((item: any) => item.isSelected === 1) ? 'block' : 'none' }}
                                >
                                    <i className="bi bi-trash fs-5 me-2"></i>
                                    {getValueFromResource('Delete', dispatch, t)}
                                </button>
                            </>
                        }
                    </div>
                </div>

                <div className="card-Report-body">
                    <div className="table-responsive">
                        <table className="table table-row-bordered align-middle gs-1 gy-1 gx-1">
                            <thead>
                                <tr className="thHeading align-middle">
                                    <th style={{ width: '20%' }}>
                                        {getValueFromResource('Policy Name', dispatch, t)}
                                        {sortColumn === 'AttendancePolicyName' ?
                                            sortOrder === 'asc' ?
                                                <span className="headerSortIconBG cursor" onClick={() => onHandleClientSideSorting("desc", "AttendancePolicyName")}>
                                                    <i className="ki-outline ki-arrow-up sortIconColor fs-5 sortMT"></i>
                                                </span> :
                                                <span className="headerSortIconBG cursor" onClick={() => onHandleClientSideSorting("asc", "AttendancePolicyName")}>
                                                    <i className="ki-outline ki-arrow-down sortIconColor fs-5 sortMT"></i>
                                                </span>
                                            :
                                            <>
                                                <span className="cursor" onClick={() => onHandleClientSideSorting("asc", "AttendancePolicyName")}>
                                                    <i className="bi bi-sort-up fs-6 headerSortIcon"></i>
                                                </span>
                                                <span className="cursor" onClick={() => onHandleClientSideSorting("desc", "AttendancePolicyName")}>
                                                    <i className="bi bi-sort-down fs-6 headerSortIcon"></i>
                                                </span>
                                            </>
                                        }
                                    </th>
                                    <th style={{ width: '30%' }}>
                                        {getValueFromResource('Description', dispatch, t)}
                                        {sortColumn === 'Description' ?
                                            sortOrder === 'asc' ?
                                                <span className="headerSortIconBG cursor" onClick={() => onHandleClientSideSorting("desc", "Description")}>
                                                    <i className="ki-outline ki-arrow-up sortIconColor fs-5 sortMT"></i>
                                                </span> :
                                                <span className="headerSortIconBG cursor" onClick={() => onHandleClientSideSorting("asc", "Description")}>
                                                    <i className="ki-outline ki-arrow-down sortIconColor fs-5 sortMT"></i>
                                                </span>
                                            :
                                            <>
                                                <span className="cursor" onClick={() => onHandleClientSideSorting("asc", "Description")}>
                                                    <i className="bi bi-sort-up fs-6 headerSortIcon"></i>
                                                </span>
                                                <span className="cursor" onClick={() => onHandleClientSideSorting("desc", "Description")}>
                                                    <i className="bi bi-sort-down fs-6 headerSortIcon"></i>
                                                </span>
                                            </>
                                        }
                                    </th>
                                    <th style={{ width: '10%' }}>
                                        {getValueFromResource('Late Arrival', dispatch, t)}
                                    </th>
                                    <th style={{ width: '10%' }}>
                                        {getValueFromResource('Early Departure', dispatch, t)}
                                    </th>
                                    <th style={{ width: '15%' }}>
                                        {getValueFromResource('Effective From', dispatch, t)}
                                    </th>
                                    <th style={{ width: '15%' }}>
                                        {getValueFromResource('Status', dispatch, t)}
                                    </th>
                                    <th className='w-10 text-center'></th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.length > 0 && currentItems.map((policyDetail: any, i: any) => {
                                    return (
                                        <tr key={i}>
                                            <td
                                                style={{ cursor: 'pointer', color: 'inherit', textDecoration: 'none' }}
                                                onClick={permissions?.IsEdit ? () => navigateToAttendancePolicyEntries(policyDetail.AccountAttendancePolicyId) : undefined}
                                            >
                                                {policyDetail.AttendancePolicyName}
                                            </td>
                                            <td className="text-truncate" style={{ maxWidth: "200px" }}>
                                                {policyDetail.Description || "-"}
                                            </td>
                                            <td>
                                                {policyDetail.LateArrivalMinutes !== undefined && policyDetail.LateArrivalMinutes !== null
                                                    ? `${policyDetail.LateArrivalMinutes} mins`
                                                    : "-"}
                                            </td>
                                            <td>
                                                {policyDetail.EarlyDepartureMinutes !== undefined && policyDetail.EarlyDepartureMinutes !== null
                                                    ? `${policyDetail.EarlyDepartureMinutes} mins`
                                                    : "-"}
                                            </td>
                                            <td>
                                                {policyDetail.EffectiveFromDate ? moment(policyDetail.EffectiveFromDate).format('DD/MM/YYYY') : "-"}
                                            </td>

                                            <td>
                                                <span className={clsx(
                                                    'badge px-3 py-2 fs-8 fw-bold',
                                                    (policyDetail.AttendancePolicyStatusId === 1 || policyDetail.Status === "Active")
                                                        ? "badge-light-success text-success" :
                                                        (policyDetail.AttendancePolicyStatusId === 3 || policyDetail.Status === "Archived" || policyDetail.Status === "InActive")
                                                            ? "badge-light-warning text-warning" :
                                                            "badge-light-secondary text-gray-600"
                                                )}>
                                                    {policyDetail.Status || (
                                                        policyDetail.AttendancePolicyStatusId === 1 ? 'Active' :
                                                            policyDetail.AttendancePolicyStatusId === 3 ? 'Archived' :
                                                                policyDetail.AttendancePolicyStatusId === 2 ? 'Draft' : 'Draft'
                                                    )}
                                                </span>
                                            </td>
                                            <td className='text-center'>
                                                <Dropdown style={{ marginLeft: 1 }} className={clsx('cursor-pointer symbol')} data-kt-menu-attach='parent' data-kt-menu-placement='bottom-end'>
                                                    <Dropdown.Toggle variant="transparent" className="btn btn-icon btn-active-light-dark btn-sm ms-0">
                                                        <i className="bi bi-three-dots fs-7"></i>
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 px-1 py-1 fs-2 w-150px'>
                                                        {permissions?.IsEdit &&
                                                            <div className='menu-hover-bg-sec'>
                                                                <Dropdown.Item className="menu-item me-lg-0" onClick={() => navigateToAttendancePolicyEntries(policyDetail.AccountAttendancePolicyId)}>
                                                                    <span className="menu-icon custom-menu mb-2">
                                                                        <i className="bi bi-pencil-square fs-6 iconColor align"></i>
                                                                    </span>
                                                                    <span className="menu-title custom-menu-fontsize">
                                                                        {getValueFromResource('Edit', dispatch, t)}
                                                                    </span>
                                                                </Dropdown.Item>
                                                            </div>
                                                        }
                                                        {permissions?.IsDelete && permissions?.IsEdit && <div className='separator mb-1 mt-1 opacity-75'></div>}
                                                        {permissions?.IsDelete && (
                                                            <div className='menu-hover-bg-danger'>
                                                                <Dropdown.Item className="menu-item me-lg-0" onClick={() => onDeleteClick(policyDetail)}>
                                                                    <span className="menu-icon custom-menu">
                                                                        <i className="bi bi-trash fs-6 delete-menu-icon-color align"></i>
                                                                    </span>
                                                                    <span className="menu-title delete-menu custom-menu-fontsize">
                                                                        {getValueFromResource('Delete', dispatch, t)}
                                                                    </span>
                                                                </Dropdown.Item>
                                                            </div>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {currentItems.length === 0 &&
                            <div>
                                <p className='noDataFound'>
                                    {getValueFromResource('No Record Found', dispatch, t)}
                                </p>
                            </div>
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default AttendancePolicyList;